import React from "react";
import { LegalLayout, LegalSection } from "./LegalLayout";

export default function Privacy() {
  return (
    <LegalLayout title="Privacy Policy" updated="June 15, 2026" testId="privacy-page">
      <LegalSection title="1. Who We Are">
        <p>
          Newground AI ("Newground," "we," "us," or "our") operates the Newground platform, an AI growth
          system for real estate agents that provides AI-powered lead generation, ad creative tools, and
          messaging automation. Newground AI is based in the United States. This Privacy Policy explains
          what information we collect, how we use it, and the choices you have.
        </p>
        <p>
          For any privacy questions or requests, contact us at{" "}
          <a href="mailto:james@newgroundai.com" className="text-[#1D6AFF] hover:underline">james@newgroundai.com</a>.
        </p>
      </LegalSection>

      <LegalSection title="2. Information We Collect">
        <p><span className="font-semibold text-slate-900">Account information.</span> When you create an account, we collect your name, email address, and a password (stored only as a secure cryptographic hash; we never store your plaintext password). If you sign up with Google, we receive your name, email address, and profile picture from Google.</p>
        <p><span className="font-semibold text-slate-900">Business and profile information.</span> Information you add to your profile or during onboarding, such as your brokerage, market area, and business goals.</p>
        <p><span className="font-semibold text-slate-900">Payment information.</span> Payments are processed by Stripe. Your full card number never touches our servers; we only receive your subscription status, plan, and billing identifiers from Stripe.</p>
        <p><span className="font-semibold text-slate-900">Content you create.</span> Ad campaigns, uploaded images, AI-generated ad hooks and creatives, and messages you send or receive through the platform.</p>
        <p><span className="font-semibold text-slate-900">Lead and contact data.</span> Names, phone numbers, and conversation history for leads you generate or manage through Newground. You are the controller of this data; we process it on your behalf to provide the service.</p>
        <p><span className="font-semibold text-slate-900">Usage and device data.</span> Log data such as IP address, browser type, pages viewed, and actions taken, used for security, debugging, and improving the product.</p>
      </LegalSection>

      <LegalSection title="3. How We Use Your Information">
        <p>We use your information to:</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>Provide, operate, and improve the Newground platform;</li>
          <li>Process subscriptions and payments through Stripe;</li>
          <li>Generate AI content (ad hooks, creatives, message drafts) using AI model providers;</li>
          <li>Send and receive SMS messages on your behalf through our telephony provider;</li>
          <li>Send transactional emails (welcome, password reset, security notices) and, with your consent, product updates;</li>
          <li>Secure accounts, prevent fraud and abuse, and comply with legal obligations.</li>
        </ul>
        <p>We do not sell your personal information.</p>
      </LegalSection>

      <LegalSection title="4. AI Processing">
        <p>
          When you use AI features (such as the Creative Maker or "Answer with AI"), the text and context you
          provide is sent to our AI model providers (including Anthropic and OpenAI, via a managed
          integration) solely to generate your requested output. We do not permit these providers to use your
          content to train their models. Generated outputs are stored in your account so you can reuse them.
        </p>
      </LegalSection>

      <LegalSection title="5. Service Providers We Share Data With">
        <ul className="list-disc pl-5 space-y-1.5">
          <li><span className="font-semibold text-slate-900">Stripe</span>: payment and subscription processing;</li>
          <li><span className="font-semibold text-slate-900">Twilio</span>: sending and receiving SMS messages;</li>
          <li><span className="font-semibold text-slate-900">Resend</span>: transactional email delivery;</li>
          <li><span className="font-semibold text-slate-900">Anthropic / OpenAI</span>: AI content generation;</li>
          <li><span className="font-semibold text-slate-900">Google</span>: optional sign-in with Google;</li>
          <li><span className="font-semibold text-slate-900">Meta Platforms</span>: if you connect your ad account, campaign data is exchanged with Meta to run your ads;</li>
          <li><span className="font-semibold text-slate-900">Cloud hosting and database providers</span>: to store and serve your data securely.</li>
        </ul>
        <p>
          Each provider only receives the data necessary to perform its function and is bound by its own
          contractual and legal obligations. We may also disclose information if required by law, or in
          connection with a merger, acquisition, or sale of assets (with notice to you).
        </p>
      </LegalSection>

      <LegalSection title="6. Cookies & Sessions">
        <p>
          We use strictly necessary cookies and tokens to keep you signed in and secure your session. We do
          not use third-party advertising cookies on our site. You can clear cookies in your browser at any
          time, though you will need to sign in again.
        </p>
      </LegalSection>

      <LegalSection title="7. Data Retention">
        <p>
          We retain your account data for as long as your account is active. If you delete your account, we
          delete or anonymize your personal data within 30 days, except where we must retain records for
          legal, tax, or fraud-prevention purposes. Billing records are retained as required by law.
        </p>
      </LegalSection>

      <LegalSection title="8. Security">
        <p>
          We use industry-standard safeguards including encryption in transit (TLS), hashed passwords,
          token-based session management with server-side invalidation, and access controls. No system is
          100% secure, so we encourage you to use a strong, unique password.
        </p>
      </LegalSection>

      <LegalSection title="9. Your Rights">
        <p>
          Depending on where you live (including under the GDPR and the California Consumer Privacy Act),
          you may have the right to access, correct, export, or delete your personal data, object to or
          restrict certain processing, and opt out of marketing communications. To exercise any of these
          rights, email{" "}
          <a href="mailto:james@newgroundai.com" className="text-[#1D6AFF] hover:underline">james@newgroundai.com</a>{" "}
          and we respond within 30 days. California residents: we do not sell or share personal information as
          defined by the CCPA/CPRA.
        </p>
      </LegalSection>

      <LegalSection title="10. SMS, Calls & Messaging Consent">
        <p>
          <span className="font-semibold text-slate-900">Your consent.</span> When you provide your mobile
          number to Newground — for example, when you activate a calling &amp; texting number or turn on SMS
          lead alerts — you consent to receive calls and text messages from Newground related to your account,
          your leads, and the service, including new-lead alerts and account notifications. Message frequency
          varies based on your lead activity. <span className="font-semibold text-slate-900">Message and data
          rates may apply.</span> You can reply <span className="font-semibold text-slate-900">STOP</span> at
          any time to opt out of texts, or <span className="font-semibold text-slate-900">HELP</span> for help.
          Opting out of texts does not affect your ability to receive calls or to use the rest of the service.
        </p>
        <p>
          <span className="font-semibold text-slate-900">Leads you contact.</span> If you use Newground's
          messaging features to reach your own leads, you are responsible for having the appropriate consent to
          contact them. Recipients can opt out at any time by replying STOP; we honor opt-outs automatically and
          maintain suppression lists as required by carrier and legal rules.
        </p>
        <p>
          <span className="font-semibold text-slate-900">We never share your mobile data.</span> We do not sell
          or share your mobile phone number or your SMS opt-in / consent information with any third parties or
          affiliates for their own marketing purposes. Phone numbers are shared only with our telephony provider
          (Twilio) strictly to deliver the calling and messaging you request.
        </p>
      </LegalSection>

      <LegalSection title="11. Children's Privacy">
        <p>
          Newground is a business tool intended for users 18 years or older. We do not knowingly collect
          personal information from children under 18.
        </p>
      </LegalSection>

      <LegalSection title="12. Changes to This Policy">
        <p>
          We may update this Privacy Policy from time to time. If we make material changes, we will notify
          you by email or an in-app notice before the changes take effect. The "Last updated" date at the top
          reflects the most recent revision.
        </p>
      </LegalSection>

      <LegalSection title="13. Contact Us">
        <p>
          Newground AI · United States<br />
          Email:{" "}
          <a href="mailto:james@newgroundai.com" className="text-[#1D6AFF] hover:underline">james@newgroundai.com</a>
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
